/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NotificationService } from '@alfresco/adf-core';
import { PageHeaderComponent, PageStateComponent, ThaiDatePipe } from '@alfresco/aca-shared';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { fileKindClass, fileKindIcon, InboxPart, MyTasksService, TaskKind, TaskRow } from './my-tasks.service';
import { WorkflowService } from './workflow.service';

export type WorkTab = 'all' | 'todo' | 'approve' | 'review' | 'task' | 'done';

/**
 * My Work (WF-06) — the user's single inbox.
 *
 * Merges what used to be the separate "My Tasks" and "Approvals" screens:
 * every row is real Activiti data — open tasks assigned to the user, the
 * pooled tasks they can claim, and the processes they started (running and
 * finished). Tabs and counters are computed over that one set, so the same
 * row answers "todo" and "approve" at once. Opening a task row goes to the
 * task page; opening a process row goes to the tracking page.
 */
@Component({
  selector: 'app-my-work',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatProgressBarModule,
    PageHeaderComponent,
    PageStateComponent,
    ThaiDatePipe,
    TranslatePipe
  ],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './my-work.component.html',
  styles: []
})
export class MyWorkComponent implements OnInit {
  private readonly tasks = inject(MyTasksService);
  private readonly wf = inject(WorkflowService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly notification = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  readonly loading = signal(true);
  /** The work to do could not be read — said on the page, not shown as "no tasks". */
  readonly loadFailed = signal(false);
  /** Lists that could not be read while the rest could — named in a note above them. */
  readonly partsMissing = signal<InboxPart[]>([]);
  readonly working = signal(false);
  readonly rows = signal<TaskRow[]>([]);
  readonly tab = signal<WorkTab>('all');
  /** The info banner stays dismissed across visits. */
  readonly bannerOn = signal(localStorage.getItem('aca.mywork.banner') !== 'off');
  /** The extra-filters row (due date + priority) folds away behind one button. */
  readonly moreOpen = signal(false);
  readonly selected = signal<ReadonlySet<string>>(new Set());
  readonly page = signal(1);
  readonly sortDue = signal<'asc' | 'desc'>('asc');

  search = '';
  filterWorkflow = '';
  filterKind = '';
  filterState = '';
  filterDue = '';
  filterPriority = '';
  pageSize = 10;
  readonly pageSizes = [10, 25, 50, 100];

  readonly tabs: { id: WorkTab; key: string }[] = [
    { id: 'all', key: 'DMS.MYWORK.TAB_ALL' },
    { id: 'todo', key: 'DMS.MYWORK.TAB_TODO' },
    { id: 'approve', key: 'DMS.MYWORK.TAB_APPROVE' },
    { id: 'review', key: 'DMS.MYWORK.TAB_REVIEW' },
    { id: 'task', key: 'DMS.MYWORK.TAB_TASK' },
    { id: 'done', key: 'DMS.MYWORK.TAB_DONE' }
  ];

  /** Workflow names present in the data — the filter's options. */
  readonly workflows = computed(() => [...new Set(this.rows().map((r) => this.wfName(r)))].sort());

  /** search and the filters are plain ngModel fields — a method re-evaluates on CD. */
  visible(): TaskRow[] {
    const term = this.search.trim().toLowerCase();
    const tab = this.tab();
    const out = this.rows().filter((r) => {
      const kind = this.tasks.kindOf(r);
      const inTab =
        tab === 'all' ||
        (tab === 'todo' && r.state !== 'completed' && r.state !== 'active') ||
        (tab === 'done' && (r.state === 'completed' || r.state === 'done')) ||
        (tab === 'approve' && kind === 'approve' && !this.isDone(r)) ||
        (tab === 'review' && kind === 'review' && !this.isDone(r)) ||
        (tab === 'task' && kind === 'task' && !this.isDone(r));
      if (!inTab) {
        return false;
      }
      if (
        term &&
        !(r.docName ?? '').toLowerCase().includes(term) &&
        !this.wfName(r).toLowerCase().includes(term) &&
        !(r.name ?? '').toLowerCase().includes(term)
      ) {
        return false;
      }
      if (this.filterWorkflow && this.wfName(r) !== this.filterWorkflow) {
        return false;
      }
      if (this.filterKind && kind !== this.filterKind) {
        return false;
      }
      if (this.filterState && this.stateKey(r) !== this.filterState) {
        return false;
      }
      if (this.filterPriority && String(r.priority) !== this.filterPriority) {
        return false;
      }
      if (this.filterDue === 'overdue' && !this.overdue(r)) {
        return false;
      }
      if (this.filterDue === 'week' && !this.dueThisWeek(r)) {
        return false;
      }
      return true;
    });
    // the artwork's due column sorts soonest-first; undated rows always stay last
    const dir = this.sortDue() === 'asc' ? 1 : -1;
    out.sort((a, b) => {
      const ta = a.dueAt ? new Date(a.dueAt).getTime() : null;
      const tb = b.dueAt ? new Date(b.dueAt).getTime() : null;
      if (ta === null || tb === null) {
        return ta === null ? (tb === null ? 0 : 1) : -1;
      }
      return (ta - tb) * dir;
    });
    return out;
  }

  /** One page of the filtered rows — the table's actual datasource. */
  paged(): TaskRow[] {
    const v = this.visible();
    const max = Math.max(1, Math.ceil(v.length / this.pageSize));
    const p = Math.min(this.page(), max);
    return v.slice((p - 1) * this.pageSize, p * this.pageSize);
  }

  pageCount(): number {
    return Math.max(1, Math.ceil(this.visible().length / this.pageSize));
  }

  /** Page buttons — a window of at most 5 around the current page. */
  pageNums(): number[] {
    const total = this.pageCount();
    const cur = Math.min(this.page(), total);
    const start = Math.max(1, Math.min(cur - 2, total - 4));
    return Array.from({ length: Math.min(5, total) }, (_, i) => start + i);
  }

  rangeFrom(): number {
    return this.visible().length ? (Math.min(this.page(), this.pageCount()) - 1) * this.pageSize + 1 : 0;
  }

  rangeTo(): number {
    return Math.min(this.rangeFrom() + this.pageSize - 1, this.visible().length);
  }

  goPage(p: number): void {
    this.page.set(Math.max(1, Math.min(p, this.pageCount())));
  }

  /** Filters, search and tabs all jump back to page 1. */
  resetPage(): void {
    this.page.set(1);
  }

  toggleDueSort(): void {
    this.sortDue.set(this.sortDue() === 'asc' ? 'desc' : 'asc');
  }

  dismissBanner(): void {
    this.bannerOn.set(false);
    localStorage.setItem('aca.mywork.banner', 'off');
  }

  /* ── row selection (checkbox column) ─────────────────────────────────── */
  isSel(r: TaskRow): boolean {
    return this.selected().has(r.id);
  }

  allSel(): boolean {
    const page = this.paged();
    return page.length > 0 && page.every((r) => this.selected().has(r.id));
  }

  toggleSel(r: TaskRow, on: boolean): void {
    const next = new Set(this.selected());
    if (on) {
      next.add(r.id);
    } else {
      next.delete(r.id);
    }
    this.selected.set(next);
  }

  toggleAllSel(on: boolean): void {
    const next = new Set(this.selected());
    for (const r of this.paged()) {
      if (on) {
        next.add(r.id);
      } else {
        next.delete(r.id);
      }
    }
    this.selected.set(next);
  }

  countFor(tab: WorkTab): number {
    const all = this.rows();
    switch (tab) {
      case 'all':
        return all.length;
      case 'todo':
        return all.filter((r) => r.state !== 'completed' && r.state !== 'active').length;
      case 'done':
        return all.filter((r) => this.isDone(r)).length;
      case 'approve':
        return all.filter((r) => this.tasks.kindOf(r) === 'approve' && !this.isDone(r)).length;
      case 'review':
        return all.filter((r) => this.tasks.kindOf(r) === 'review' && !this.isDone(r)).length;
      case 'task':
        return all.filter((r) => this.tasks.kindOf(r) === 'task' && !this.isDone(r)).length;
    }
  }

  isDone(r: TaskRow): boolean {
    return r.state === 'completed' || r.state === 'done';
  }

  ngOnInit(): void {
    // ?type=approval keeps the retired /approvals route working
    if (this.route.snapshot.queryParamMap.get('type') === 'approval') {
      this.tab.set('approve');
    }
    // /workflows/track opens the same inbox filtered to running processes
    if (this.route.snapshot.data['track'] || this.route.snapshot.queryParamMap.get('type') === 'track') {
      this.tab.set('all');
      this.filterState = 'running';
    }
    this.reload();
  }

  reload(): void {
    this.loading.set(true);
    this.tasks.inbox().subscribe({
      next: ({ todo, pooled, running, done, failedParts }) => {
        const mine = new Set(todo.map((t) => t.id));
        const extra = pooled.filter((p) => !mine.has(p.id));
        this.rows.set([...todo, ...extra, ...running, ...done]);
        this.selected.set(new Set());
        // the page itself fails only when the work to do cannot be read; a
        // missing list of started or finished flows is named above the rest
        const tasksLost = failedParts.includes('todo') && failedParts.includes('pooled');
        this.loadFailed.set(tasksLost);
        this.partsMissing.set(tasksLost ? [] : failedParts);
        this.loading.set(false);
      },
      error: () => {
        this.rows.set([]);
        this.loadFailed.set(true);
        this.partsMissing.set([]);
        this.loading.set(false);
      }
    });
  }

  switchTab(tab: WorkTab): void {
    this.tab.set(tab);
    this.page.set(1);
    this.selected.set(new Set());
  }

  /** Workflow label from the definition id — "activitiReview:1:4" → readable. */
  wfName(r: TaskRow): string {
    const raw = String(r.processDefinitionId ?? '').split(':')[0];
    if (!raw) {
      return r.name || '—';
    }
    return raw.replace(/([a-z])([A-Z])/g, '$1 $2');
  }

  kind(r: TaskRow): TaskKind {
    return this.tasks.kindOf(r);
  }

  kindLabel(r: TaskRow): string {
    const kind = this.kind(r);
    return kind === 'approve' ? 'DMS.MYWORK.KIND_APPROVE' : kind === 'review' ? 'DMS.MYWORK.KIND_REVIEW' : 'DMS.MYWORK.KIND_TASK';
  }

  stateKey(r: TaskRow): string {
    if (r.state === 'unclaimed') {
      return 'pooled';
    }
    return this.isDone(r) ? 'done' : r.state === 'active' ? 'running' : 'open';
  }

  priorityLabel(p: number): string {
    return p === 1 ? 'DMS.MYWORK.P_HIGH' : p === 3 ? 'DMS.MYWORK.P_LOW' : 'DMS.MYWORK.P_MEDIUM';
  }

  priorityClass(p: number): string {
    return p === 1 ? 'aca-work__chip--high' : p === 3 ? 'aca-work__chip--low' : 'aca-work__chip--med';
  }

  overdue(r: TaskRow): boolean {
    return !!r.dueAt && !this.isDone(r) && new Date(r.dueAt).getTime() < Date.now();
  }

  private dueThisWeek(r: TaskRow): boolean {
    if (!r.dueAt) {
      return false;
    }
    const t = new Date(r.dueAt).getTime();
    return t >= Date.now() && t <= Date.now() + 7 * 86400000;
  }

  badge(r: TaskRow): string {
    const ext = (r.docName ?? '').split('.').pop()?.toLowerCase() ?? '';
    const map: Record<string, string> = { pdf: 'PDF', doc: 'W', docx: 'W', xls: 'X', xlsx: 'X', ppt: 'P', pptx: 'P', txt: 'TXT' };
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) {
      return 'IMG';
    }
    return map[ext] ?? 'FILE';
  }

  /** Colored file tile by extension — the artwork's PDF-red / DOC-blue / XLS-green. */
  fileClass(r: TaskRow): string {
    return fileKindClass(r.docName);
  }

  fileIcon(r: TaskRow): string {
    return fileKindIcon(r.docName);
  }

  /** Line under the file name: the document code when the model has one. */
  docSub(r: TaskRow): string {
    return r.docCode || r.assignee || '—';
  }

  /** Workflow's second line — the task description when there is one. */
  wfSub(r: TaskRow): string {
    return r.description || '';
  }

  /**
   * How the due cell reads: today/tomorrow get a word, anything inside a week
   * gets a countdown, an unfinished overdue row reads red.
   */
  dueMode(r: TaskRow): 'none' | 'over' | 'today' | 'tomorrow' | 'week' | 'date' {
    if (!r.dueAt) {
      return 'none';
    }
    if (this.overdue(r)) {
      return 'over';
    }
    const d = new Date(r.dueAt);
    const now = new Date();
    // finished or already-past dues read as a plain date — no countdown
    if (this.isDone(r) || d.getTime() < now.getTime()) {
      return 'date';
    }
    if (d.toDateString() === now.toDateString()) {
      return 'today';
    }
    const tomorrow = new Date(now.getTime() + 86400000);
    if (d.toDateString() === tomorrow.toDateString()) {
      return 'tomorrow';
    }
    return d.getTime() - now.getTime() <= 7 * 86400000 ? 'week' : 'date';
  }

  dueLeftHours(r: TaskRow): number {
    return r.dueAt ? Math.max(1, Math.ceil((new Date(r.dueAt).getTime() - Date.now()) / 3600000)) : 0;
  }

  dueLeftMin(r: TaskRow): number {
    return r.dueAt ? Math.max(1, Math.ceil((new Date(r.dueAt).getTime() - Date.now()) / 60000)) : 0;
  }

  dueLeftDays(r: TaskRow): number {
    return r.dueAt ? Math.max(1, Math.ceil((new Date(r.dueAt).getTime() - Date.now()) / 86400000)) : 0;
  }

  /** An unfinished overdue row reads "เกินกำหนด" in red instead of its raw state. */
  chipKey(r: TaskRow): string {
    return this.overdue(r) ? 'overdue' : this.stateKey(r);
  }

  chipIcon(r: TaskRow): string {
    switch (this.chipKey(r)) {
      case 'open':
        return 'schedule';
      case 'pooled':
        return 'groups';
      case 'running':
        return 'autorenew';
      case 'overdue':
        return 'error_outline';
      default:
        return 'check_circle';
    }
  }

  initial(name: string): string {
    return (name || '?').trim().charAt(0).toUpperCase();
  }

  /** Task rows open the task page; process rows open tracking. */
  openRow(r: TaskRow): void {
    if (r.id === r.processId) {
      void this.router.navigate(['/workflows/instances', r.processId]);
    } else {
      void this.router.navigate(['/my-work/task', r.id]);
    }
  }

  openDoc(r: TaskRow, event: Event): void {
    event.stopPropagation();
    if (r.docId) {
      void this.router.navigate(['/document', r.docId]);
    }
  }

  claimable(r: TaskRow): boolean {
    return r.state === 'unclaimed' || (r.candidateGroups?.length ?? 0) > 0;
  }

  claim(r: TaskRow, event: Event): void {
    event.stopPropagation();
    this.working.set(true);
    this.wf.claimTask(r.id).subscribe({
      next: () => {
        this.working.set(false);
        this.notification.showInfo(this.translate.instant('DMS.MYWORK.CLAIMED'));
        this.reload();
      },
      error: () => {
        this.working.set(false);
        this.notification.showError(this.translate.instant('DMS.MYWORK.CLAIM_FAILED'));
      }
    });
  }
}
